// src/components/profile/roi/Waterfall.jsx
import React, { useState, Fragment } from 'react';
import { ChevronDown, ChevronUp, BarChart3, Info } from 'lucide-react';

export default function Waterfall({ drivers = [], investment = 0, scenario = 'base' }) {
  const [isOpen, setIsOpen] = useState(true);
  
  const formatCurrency = (value) => {
    if (!value || isNaN(value)) return '$0';
    const absValue = Math.abs(value);
    if (absValue >= 1000000) return `$${(value/1000000).toFixed(1)}M`;
    if (absValue >= 1000) return `$${Math.round(value/1000)}K`;
    return `$${value.toFixed(0)}`;
  };
  
  // Calculate net value
  const totalDrivers = drivers.reduce((sum, d) => sum + (d.value || 0), 0);
  const netValue = totalDrivers - Math.abs(investment);
  
  // Combine all bars for display
  const allBars = [
    ...drivers.map(d => ({
      ...d,
      isDriver: true
    })),
    { 
      label: 'Investment', 
      value: -Math.abs(investment), 
      color: 'red', 
      category: 'Cost',
      isFixed: true 
    },
    { 
      label: 'Net Value', 
      value: netValue, 
      color: netValue > 0 ? 'emerald' : 'red', 
      category: 'Total',
      isTotal: true 
    }
  ];
  
  // Find max value for scaling
  const maxValue = Math.max(...allBars.map(b => Math.abs(b.value)));
  
  return (
    <div className="bg-white border border-gray-200 rounded-lg">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-6 py-4 flex items-center justify-between hover:bg-gray-50 transition-colors"
      >
        <h3 className="font-semibold text-gray-900 flex items-center gap-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          Waterfall Analysis
          {scenario !== 'base' && (
            <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full ml-2">
              {scenario} scenario
            </span>
          )}
        </h3>
        {isOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
      </button>
      
      {isOpen && (
        <div className="px-6 pb-6">
          {maxValue > 0 ? (
            <div className="space-y-4">
              {/* Chart - Iteration 6: Enhanced with labels and dividers */}
              <div className="relative flex items-end justify-between gap-2 h-80 px-4 pt-4">
                {allBars.map((bar, idx) => {
                  const barHeight = (Math.abs(bar.value) / maxValue) * 200;
                  const bgColor = bar.color === 'green' ? 'bg-green-500' :
                                 bar.color === 'blue' ? 'bg-blue-500' :
                                 bar.color === 'emerald' ? 'bg-emerald-500' :
                                 'bg-red-500';
                  
                  // Determine section breaks for vertical dividers
                  const prevBar = idx > 0 ? allBars[idx - 1] : null;
                  const isSectionBreak = prevBar && (
                    (prevBar.isDriver && bar.category === 'Cost') ||
                    (prevBar.category === 'Cost' && bar.isTotal)
                  );
                  
                  return (
                    <Fragment key={idx}>
                      {/* Iteration 6: Vertical separator between Benefits → Costs → Net */}
                      {isSectionBreak && (
                        <div 
                          className="absolute h-full w-px bg-gray-300 opacity-40 z-0"
                          style={{ 
                            left: `${(idx / allBars.length) * 100}%`,
                            transform: 'translateX(-50%)'
                          }}
                        ></div>
                      )}
                      
                      <div className="flex-1 flex flex-col items-center group relative z-10">
                        {/* Iteration 6: Numeric labels directly on bars */}
                        {barHeight > 20 && (
                          <div 
                            className="absolute text-[10px] font-bold whitespace-nowrap z-10"
                            style={{
                              bottom: `${barHeight + 4}px`,
                              color: bar.value > 0 ? '#059669' : '#dc2626',
                              transform: 'translateX(-50%)',
                              left: '50%'
                            }}
                          >
                            {bar.value > 0 ? '+' : ''}{formatCurrency(bar.value)}
                            {bar.isTotal && ' Net Value'}
                          </div>
                        )}
                        
                        {/* Bar */}
                        <div 
                          className={`w-full ${bgColor} rounded-t transition-all duration-500 hover:opacity-80 relative flex items-end justify-center`}
                          style={{ height: `${barHeight}px`, minHeight: '20px' }}
                        >
                          {/* Iteration 6: Label inside bar for small bars */}
                          {barHeight <= 20 && (
                            <div 
                              className="text-[9px] font-bold text-white px-1"
                              style={{ writingMode: 'vertical-rl', textOrientation: 'mixed' }}
                            >
                              {bar.value > 0 ? '+' : ''}{formatCurrency(bar.value)}
                            </div>
                          )}
                          
                          {/* Scenario indicator on variable bars */}
                          {bar.isDriver && scenario !== 'base' && (
                            <div className="absolute -top-6 left-1/2 transform -translate-x-1/2 text-xs bg-blue-100 text-blue-700 px-1 py-0.5 rounded">
                              {scenario === 'conservative' ? '0.7×' : '1.3×'}
                            </div>
                          )}
                        </div>
                        
                        {/* Label */}
                        <div className="text-xs mt-2 text-center text-gray-600 max-w-[80px] font-medium">
                          {bar.label}
                        </div>
                        
                        {/* Hover tooltip */}
                        <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
                          <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap shadow-lg">
                            {bar.label}: {formatCurrency(Math.abs(bar.value))}
                            {bar.category && <div className="text-gray-300">{bar.category}</div>}
                          </div>
                        </div>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
              
              {/* Legend */}
              <div className="flex justify-between items-center pt-4 border-t">
                <div className="flex gap-4 text-xs">
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-green-500 rounded"></span> Revenue
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-blue-500 rounded"></span> Savings
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-red-500 rounded"></span> Investment
                  </span>
                  <span className="flex items-center gap-1">
                    <span className="w-3 h-3 bg-emerald-500 rounded"></span> Net Value
                  </span>
                </div>
                
                {/* Summary stats */}
                <div className="flex items-center gap-4 text-sm">
                  <span className="text-gray-600">
                    Total Benefits: <span className="font-bold text-green-600">{formatCurrency(totalDrivers)}</span>
                  </span>
                  <span className="text-gray-600">
                    Net: <span className={`font-bold ${netValue > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                      {formatCurrency(netValue)}
                    </span>
                  </span>
                </div>
              </div>
              
              {/* Scenario note */}
              {scenario !== 'base' && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 flex items-start gap-2">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5" />
                  <div className="text-xs text-blue-800">
                    <strong>{scenario === 'conservative' ? 'Conservative' : 'Aggressive'} scenario:</strong> Benefits 
                    adjusted by {scenario === 'conservative' ? '0.7×' : '1.3×'} multiplier. 
                    Investment remains constant at {formatCurrency(investment)}.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <BarChart3 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
              <p>No driver data available</p>
              <p className="text-sm mt-2">Add projections to see waterfall analysis</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}